<template>
  <Teleport to="body">
    <Transition name="cookie-panel">
      <div
        v-if="cookieSettingsVisible"
        class="cookie-panel fixed z-1001 flex flex-col"
      >
        <!-- Close button -->
        <button
          class="cookie-close"
          aria-label="Close cookie settings"
          @click="hideCookieSettings"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>

        <!-- Title -->
        <h3 class="cookie-title">Cookie 设置</h3>

        <!-- Description -->
        <p class="cookie-desc">
          我们使用 Cookie 来提升您的体验并分析网站流量和访问行为。请阅读我们的<router-link to="/cookie-policy" class="cookie-link" @click="hideCookieSettings">Cookie 政策</router-link>。
        </p>

        <!-- Buttons -->
        <div class="cookie-actions">
          <button class="cookie-btn cookie-btn--accept" @click="acceptCookies">
            接受 Cookie
          </button>
          <button class="cookie-btn cookie-btn--reject" @click="rejectCookies">
            拒绝 Cookie
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useCookieConsent } from '@/composables/useCookieConsent'

const {
  cookieSettingsVisible,
  hasMadeDecision,
  showCookieSettings,
  hideCookieSettings,
  acceptCookies,
  rejectCookies,
} = useCookieConsent()

// Auto-show on mount if no decision yet
onMounted(() => {
  if (!hasMadeDecision()) {
    showCookieSettings()
  }
})

defineExpose({ showCookieSettings })
</script>

<style lang="scss" scoped>
.cookie-panel {
  right: 24px;
  bottom: 24px;
  width: 380px;
  max-width: calc(100vw - 48px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  padding: 24px;
  gap: 16px;
}

.cookie-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
}

.cookie-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #001234;
  line-height: 1.3;
  text-align: center;
}

.cookie-desc {
  margin: 0;
  font-size: 14px;
  color: #575859;
  line-height: 1.6;
}

.cookie-link {
  color: #071033;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.cookie-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.cookie-btn {
  flex: 1;
  height: 32px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  padding: 0 12px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &--accept {
    background: #22c3ff;
    color: #071033;
  }

  &--reject {
    background: #071033;
    color: #fff;
  }
}

/* ── Transition ── */
.cookie-panel-enter-active {
  animation: cookie-slide-in 0.35s ease-out both;
}

.cookie-panel-leave-active {
  animation: cookie-slide-out 0.25s ease-in both;
}

@keyframes cookie-slide-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes cookie-slide-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
}

@media (max-width: 480px) {
  .cookie-panel {
    right: 12px;
    bottom: 12px;
    left: 12px;
    max-width: none;
    width: auto;
  }

  .cookie-actions {
    flex-direction: column;
  }
}
</style>
